import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  StateKey,
  TransferState,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { ApiError, ApiParams, ApiRequestOptions } from './api.model';

/**
 * Thin, typed wrapper over `HttpClient` for the GogoKids API.
 *
 * - Prepends {@link environment.api.url} to every path, so callers pass
 *   `'auth/login'`, not a full URL.
 * - Every method returns a cold `Observable` that errors with {@link ApiError}
 *   on any failure.
 * - Optionally de-duplicates GETs across the SSR → browser handoff via
 *   `TransferState` (`{ cache: true }`).
 *
 * Feature services should inject this rather than `HttpClient` directly.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));
  private readonly baseUrl = environment.api.url.replace(/\/+$/, '');

  get<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    if (options?.cache) return this.cachedGet<T>(path, options);
    return this.request('GET', path, undefined, options);
  }

  post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request('POST', path, body, options);
  }

  put<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request('PUT', path, body, options);
  }

  patch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.request('PATCH', path, body, options);
  }

  delete<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.request('DELETE', path, undefined, options);
  }

  private request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    options?: ApiRequestOptions,
  ): Observable<T> {
    return this.http
      .request<T>(method, this.url(path), {
        body,
        params: this.toHttpParams(options?.params),
        headers: options?.headers,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => throwError(() => new ApiError(error))),
      ) as Observable<T>;
  }

  private cachedGet<T>(path: string, options: ApiRequestOptions): Observable<T> {
    const key: StateKey<T | null> = makeStateKey<T | null>(
      `api:GET:${path}:${JSON.stringify(options.params ?? {})}`,
    );

    const cached = this.transferState.get(key, null);
    if (cached !== null) {
      this.transferState.remove(key);
      return of(cached);
    }

    return this.request<T>('GET', path, undefined, options).pipe(
      tap((result) => {
        if (this.isServer) this.transferState.set(key, result);
      }),
    );
  }

  private url(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    return `${this.baseUrl}/${path.replace(/^\/+/, '')}`;
  }

  private toHttpParams(params?: ApiParams): HttpParams | undefined {
    if (!params) return undefined;
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) continue;
      if (Array.isArray(value)) {
        for (const item of value) httpParams = httpParams.append(key, String(item));
      } else {
        httpParams = httpParams.set(key, String(value));
      }
    }
    return httpParams;
  }
}
